import type { AssistantPlan } from '@/features/assistant/types'
import { parseAssistantPrompt } from '@/features/assistant/planner/prompt-parser'
import { deepseekPlanner } from '@/features/assistant/planner/deepseek-planner'
import {
  parseVoiceoverVideoPlan,
  type VoiceoverVideoPlan,
} from '@/features/assistant/planner/planner-schema'
import {
  createVoiceoverVideoAssistantPlan,
  type VoiceoverAssistantPreferences,
  type VoiceoverPlanningProjectSignals,
  type VoiceoverTimelineStrategy,
  type VoiceoverVideoAssistantContext,
} from '@/features/assistant/planner/voiceover-video-planner'
import {
  analyzeProjectMedia,
  type AssistantProjectMediaAnalysis,
} from '@/features/assistant/tools/media-tools'
import { useAssistantJobStore } from '@/features/assistant/job-store'
import type { AssistantToolSamplingClient } from '@/features/assistant/tool-context'
import { createLogger } from '@/shared/logging/logger'

const logger = createLogger('PlanningTools')

export interface DraftEditPlanRequest extends VoiceoverAssistantPreferences {
  prompt: string
}

export interface DraftEditPlanSamplingClient extends AssistantToolSamplingClient {}

const DRAFT_EDIT_PLAN_SYSTEM_PROMPT = [
  'You are the JIAN assistant planner.',
  'Return exactly one JSON object and no extra text.',
  'Use the current project media context when it is provided.',
  'The JSON must contain these fields:',
  '{"taskType":"voiceover_video","topic":"string","script":"string","durationSeconds":number,"aspectRatio":"9:16"|"16:9"|"1:1","tone":"natural"|"professional"|"friendly"|"energetic"|"calm","needsTts":boolean,"needsCaptions":boolean,"needsBgm":boolean,"exportDraft":boolean}',
].join('\n')

function buildDraftEditPlanUserPrompt(
  prompt: string,
  projectMediaAnalysis?: AssistantProjectMediaAnalysis,
): string {
  const sections = ['User request:', prompt.trim()]

  if (projectMediaAnalysis) {
    sections.push('Project media summary:', JSON.stringify(projectMediaAnalysis, null, 2))
  }

  return sections.join('\n')
}

async function parseSamplingPlanResponse(
  samplingClient: DraftEditPlanSamplingClient,
  request: DraftEditPlanRequest,
): Promise<VoiceoverVideoPlan> {
  const projectMediaAnalysis = await analyzeProjectMedia()
  logger.info('Requesting MCP sampling planner output', {
    userPromptLength: request.prompt.trim().length,
    mediaContextItems: projectMediaAnalysis.items.length,
    timelineItemCount: projectMediaAnalysis.timeline.itemCount,
  })
  const response = await samplingClient.createMessage({
    systemPrompt: DRAFT_EDIT_PLAN_SYSTEM_PROMPT,
    temperature: 0.2,
    maxTokens: 600,
    messages: [
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: buildDraftEditPlanUserPrompt(request.prompt, projectMediaAnalysis),
          },
        ],
      },
    ],
  })

  const content = response.content.map((item) => item.text).join('\n').trim()
  if (!content) {
    throw new Error('Sampling response did not contain message content.')
  }

  const plan = parseVoiceoverVideoPlan(content)
  logger.info('Parsed MCP sampling planner output', {
    taskType: plan.taskType,
    durationSeconds: plan.durationSeconds,
    aspectRatio: plan.aspectRatio,
    scriptLength: plan.script.length,
    needsBgm: plan.needsBgm,
  })
  return plan
}

export async function tryDraftEditPlanWithSampling(
  request: DraftEditPlanRequest,
  samplingClient?: DraftEditPlanSamplingClient,
): Promise<VoiceoverVideoPlan | null> {
  if (!samplingClient) {
    logger.info('Skipping MCP sampling planner path because no sampling client was provided.')
    return null
  }

  return parseSamplingPlanResponse(samplingClient, request)
}

export function buildPlanningProjectSignals(
  projectMediaAnalysis: AssistantProjectMediaAnalysis,
): VoiceoverPlanningProjectSignals {
  return {
    existingVisualMediaCount: projectMediaAnalysis.items.filter(
      (item) => item.kind === 'video' || item.kind === 'image',
    ).length,
    usedTimelineVisualMediaCount: projectMediaAnalysis.items.filter(
      (item) => item.isUsedOnTimeline && (item.kind === 'video' || item.kind === 'image'),
    ).length,
    transcriptReadyMediaCount: projectMediaAnalysis.media.transcriptReadyCount,
    currentTimelineItemCount: projectMediaAnalysis.timeline.itemCount,
    currentTimelineDurationSeconds: projectMediaAnalysis.timeline.totalDurationSeconds,
  }
}

export function resolveVoiceoverTimelineStrategy(params: {
  prompt: string
  projectSignals?: VoiceoverPlanningProjectSignals
}): VoiceoverTimelineStrategy {
  const hints = parseAssistantPrompt(params.prompt)

  if (hints.timelineIntent === 'create-draft') {
    return {
      mode: 'create-draft-timeline',
      preserveCurrentTimeline: true,
      reason: 'explicit-user-request',
    }
  }

  if (hints.timelineIntent === 'preserve-current') {
    return {
      mode: 'create-draft-timeline',
      preserveCurrentTimeline: true,
      reason: 'protect-existing-edit',
    }
  }

  if ((params.projectSignals?.usedTimelineVisualMediaCount ?? 0) > 0) {
    return {
      mode: 'reuse-current-timeline',
      preserveCurrentTimeline: false,
      reason: 'current-timeline-reuse',
    }
  }

  return {
    mode: 'reuse-current-timeline',
    preserveCurrentTimeline: false,
    reason: 'default-current-project',
  }
}

export async function draftEditPlan(
  request: DraftEditPlanRequest,
  samplingClient?: DraftEditPlanSamplingClient,
): Promise<AssistantPlan<VoiceoverVideoAssistantContext>> {
  const trimmedPrompt = request.prompt.trim()
  if (!trimmedPrompt) {
    throw new Error('Planner prompt cannot be empty.')
  }

  const projectMediaAnalysis = await analyzeProjectMedia()
  const sampledPlan = await tryDraftEditPlanWithSampling(request, samplingClient).catch((error) => {
    logger.warn('MCP sampling planner path failed, falling back to DeepSeek planner.', {
      message: error instanceof Error ? error.message : String(error),
    })
    return null
  })
  const plannerSource = sampledPlan ? 'sampling' : 'deepseek'
  const plannerOutput =
    sampledPlan ??
    (await deepseekPlanner.planVoiceoverVideo({
      userPrompt: trimmedPrompt,
      projectMediaAnalysis,
    }))

  const projectSignals = buildPlanningProjectSignals(projectMediaAnalysis)
  const timelineStrategy = resolveVoiceoverTimelineStrategy({
    prompt: trimmedPrompt,
    projectSignals,
  })

  return createVoiceoverVideoAssistantPlan(trimmedPrompt, plannerOutput, {
    ttsVoice: request.ttsVoice,
    bgmMode: request.bgmMode,
    selectedBgmMediaId: request.selectedBgmMediaId,
    selectedBgmMediaLabel: request.selectedBgmMediaLabel,
    selectedReferenceMediaIds: request.selectedReferenceMediaIds,
    selectedReferenceMediaLabels: request.selectedReferenceMediaLabels,
    projectSignals,
    timelineStrategy,
    plannerSource,
  })
}

export function getAssistantPlanStatus(): {
  hasDraftPlan: boolean
  hasCurrentJob: boolean
  isRunning: boolean
  planTitle: string | null
  jobStatus: string | null
  currentStepId: string | null
  currentStepTitle: string | null
  currentStepStatus: string | null
  error: string | null
} {
  const state = useAssistantJobStore.getState()

  const planTitle = state.draftPlan?.title ?? null
  const jobStatus = state.currentJob?.status ?? null
  const currentStepId = state.currentJob?.currentStepId ?? null
  const currentStep = currentStepId ? state.currentJob?.steps.find((s) => s.id === currentStepId) : null

  return {
    hasDraftPlan: state.draftPlan !== null,
    hasCurrentJob: state.currentJob !== null,
    isRunning: state.isRunning,
    planTitle,
    jobStatus,
    currentStepId,
    currentStepTitle: currentStep?.title ?? null,
    currentStepStatus: currentStep?.status ?? null,
    error: state.error,
  }
}

export function confirmDraftPlan(): {
  confirmed: boolean
  planId: string | null
  stepCount: number
} {
  const state = useAssistantJobStore.getState()
  if (!state.draftPlan) {
    return { confirmed: false, planId: null, stepCount: 0 }
  }

  useAssistantJobStore.getState().startRun()
  return {
    confirmed: true,
    planId: state.draftPlan.id,
    stepCount: state.draftPlan.steps.length,
  }
}
